/**
 * Pure helpers for builder-v1 deep-linkable dashboard tabs (spec §10, plan step 17).
 * The active tab's page id lives in the route query under TAB_QUERY_KEY; these functions
 * map between that query and the tab index with no Vue/router coupling, so the routing
 * logic in CustomDashboard.vue stays thin and this stays unit-testable.
 */

export const TAB_QUERY_KEY = 'tab';

/**
 * Resolve the tab index for a route query's tab id. Returns -1 when the query carries no
 * tab id, or the id matches no current page (an unknown / deleted deep link).
 * @param {Array<{id: string|number}>} pages
 * @param {object} query  The $route.query object.
 * @returns {number}
 */
export function resolveTabIndexFromQuery(pages, query) {
  const id = query && query[TAB_QUERY_KEY];
  if (id == null || id === '') return -1;
  return (pages || []).findIndex(p => String(p.id) === String(id));
}

/**
 * The tab to fall back to when a deep link points at a missing dashboard: the `default`
 * page when present, otherwise the first tab. -1 when there are no pages at all.
 * @param {Array<{id: string|number}>} pages
 * @returns {number}
 */
export function fallbackTabIndex(pages) {
  const list = pages || [];
  const defaultIdx = list.findIndex(p => p.id === 'default');
  if (defaultIdx > -1) return defaultIdx;
  return list.length ? 0 : -1;
}

/**
 * Left-adjacent tab to focus after deleting the tab at `deletedIndex` (spec §7). `pages`
 * is the list AFTER the delete splice. Prefers the immediate left neighbour; when the
 * deleted tab was leftmost, falls back to the `default` page, else the first tab. -1 when
 * no pages remain.
 * @param {Array<{id: string|number}>} pages  Pages AFTER the delete splice.
 * @param {number} deletedIndex               The index the deleted page occupied.
 * @returns {number}
 */
export function tabIndexAfterDeletion(pages, deletedIndex) {
  const list = pages || [];
  if (!list.length) return -1;
  const left = deletedIndex - 1;
  if (left >= 0 && left < list.length) return left;
  return fallbackTabIndex(list);
}

export default { TAB_QUERY_KEY, resolveTabIndexFromQuery, fallbackTabIndex, tabIndexAfterDeletion };
